import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { RotateCcw, PenTool } from 'lucide-react';

const SignaturePad = ({ onSave }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = '#0f172a'; // Deep Slate Ink
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    }, []);

    const startDrawing = (e) => {
        const { offsetX, offsetY } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(offsetX, offsetY);
        setIsDrawing(true);
        setIsEmpty(false);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const { offsetX, offsetY } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineTo(offsetX, offsetY);
        ctx.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
        const dataUrl = canvasRef.current.toDataURL();
        onSave(dataUrl);
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setIsEmpty(true);
        onSave(null);
    };

    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        
        let clientX, clientY;
        if (e.touches && e.touches[0]) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        // Calculate scaling factors to map CSS pixels to canvas internal resolution
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return {
            offsetX: (clientX - rect.left) * scaleX,
            offsetY: (clientY - rect.top) * scaleY
        };
    };

    return (
        <div className="space-y-4 w-full">
            <div className="relative bg-slate-50 border border-slate-200 rounded-xl overflow-hidden cursor-crosshair h-64 shadow-inner">
                <canvas
                    ref={canvasRef}
                    width={800} // Increased resolution for better ink quality
                    height={400}
                    className="w-full h-full touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                />
                
                {isEmpty && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none opacity-30">
                        <PenTool size={32} className="text-slate-400 mb-2" />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Sign in the box</span>
                    </div>
                )}
                
                <div className="absolute top-4 right-4 flex gap-2">
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={clear}
                        className="h-10 w-10 text-slate-400 hover:text-slate-900 bg-white/60 backdrop-blur-sm border border-slate-100 rounded-lg shadow-sm"
                    >
                        <RotateCcw size={16} />
                    </Button>
                </div>
            </div>
            
            <p className="text-[10px] text-gray-500 font-medium text-center italic tracking-tight">
                By signing above, you agree that this electronic signature is as legally binding as a handwritten one.
            </p>
        </div>
    );
};

export default SignaturePad;
